import { useStoreState } from "../store/useStore";
import useFetch from "../../hooks/useFetch";
import Cookies from "js-cookie";

/**
 * sessionStorage 缓存契约（PUBLIC CONTRACT）
 *
 * 与 libs/blofin-utils/chatbot/grayService.ts 共享同一份 sessionStorage 缓存，
 * 用于在同一次会话内复用 ChatbotGrayGate 已经决策出的灰度结果，避免本 Footer 重复请求。
 *
 * 缓存写入方：libs/blofin-utils/chatbot/grayService.ts （唯一写入方，本文件只读不写）
 * 修改 GRAY_CACHE_KEY / GRAY_CACHE_TTL_MS / 字段结构时，必须同步更新 grayService.ts。
 *
 * 设计取舍：
 * - 只读不写，保持单一写入方，避免跨包同时维护写入逻辑导致格式漂移。
 * - 缓存 miss 时退化为现状（直接调接口），无负作用。
 * - 任何解析异常都返回 null，绝不抛出，确保 Footer 渲染稳定。
 */
const GRAY_CACHE_KEY = "chatbot_gray_config";
const GRAY_CACHE_TTL_MS = 5 * 60 * 1000;

interface CachedGrayConfig {
  hitGray: boolean;
  regionCode: string;
  wasLoggedIn: boolean;
  ts: number;
}

function readGrayCacheSafely(isLoggedIn: boolean): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(GRAY_CACHE_KEY);
    if (!raw) return null;
    const cfg = JSON.parse(raw) as Partial<CachedGrayConfig>;
    if (typeof cfg?.hitGray !== "boolean") return null;
    if (typeof cfg?.ts !== "number") return null;
    if (Date.now() - cfg.ts > GRAY_CACHE_TTL_MS) return null;
    if (cfg.wasLoggedIn !== isLoggedIn) return null;
    return cfg.hitGray;
  } catch {
    return null;
  }
}

const useAction = (base?: string) => {
  const { baseUrl } = useStoreState();

  const { post, get } = useFetch(base ? base : baseUrl);

  const fetchJourneyUserInfo = async <T>() => {
    return post<T>(`uapi/v1/journey/user/info`);
  };

  const fetchChatbotGrayConfig = async <T>() => {
    const token = Cookies.get("token");
    const isLoggedIn = !!token;

    // 命中 ChatbotGrayGate 写入的缓存时直接复用，省一次请求且与 SDK 决策保持一致
    const cachedHitGray = readGrayCacheSafely(isLoggedIn);
    if (cachedHitGray !== null) {
      return {
        code: "200",
        data: { hitGray: cachedHitGray, regionCode: "" }
      } as unknown as T;
    }

    const path = isLoggedIn
      ? "uapi/v1/intelligent-bot/open/navigation/v2/gray-config"
      : "uapi/v1/intelligent-bot/inner/navigation/v2/gray-config";
    return get<T>(path);
  };

  return {
    fetchJourneyUserInfo,
    fetchChatbotGrayConfig
  };
};

export default useAction;
